import { Box, Stack } from "@mui/material";
import * as React from "react";
import parse from "html-react-parser";
import dayjs from "dayjs";
import { ContentListItemType, ContentType } from "./ContentListItem.types";
import {
    AvatarContainer,
    AvatarStyled,
    CountsStack,
    ListItemAvatarContainer,
    ListItemButtonContainer,
    ListItemMainSection,
    ListItemTextHeadline,
    ListItemTextRelatedPost,
    ListItemTextSubline,
    TypographyHeaderSubTitleBox,
} from "./ContentListItem.styles";
import { useMobile } from "../../../../../Infrastructure/Hooks/useMobile";
import getRelativeDate from "../../../RelativeDate/RelativeDate";
import { prepareAvatarContent } from "../../../UserAvatar/UserAvatarFactory";
import { avatarFallbackColorSelector } from "../../../../Shared/Avatar/AvatarFallbackColorSelector";
import { CommentSolidIcon } from "../../../Icon/CommentSolidIcon";
import { GetReactionIcon } from "../../../../Shared/Components/Tippspiel/Reactions/ReactionsIconsFactory";
import { aggregateReactions } from "../../../../../Infrastructure/Persistence/Reactions";
import { highlightText } from "../../../../../Infrastructure/Helpers/StringHelper";

dayjs.locale("de");

export function ContentListItem({
    content,
    onItemClick,
    avatarIconOverlay,
    stringsToHighlight,
}: ContentListItemType) {
    const { isMobile } = useMobile();
    const datePosted = dayjs(content.createdAt);
    const relativeDate = getRelativeDate(datePosted.toDate());

    const text = stringsToHighlight?.length
        ? highlightText(content.content, stringsToHighlight)
        : content.content;

    return (
        <ListItemButtonContainer
            onClick={() => onItemClick(content)}
            disableRipple={isMobile}
            showHover={!isMobile}
        >
            <ListItemMainSection>
                <ListItemAvatarContainer>
                    <AvatarContainer>
                        <AvatarStyled src={content.user.avatar ?? ""}>
                            {prepareAvatarContent(
                                avatarFallbackColorSelector(content.user.uuid),
                                content.user.gender,
                            )}
                        </AvatarStyled>
                        {avatarIconOverlay}
                    </AvatarContainer>
                </ListItemAvatarContainer>

                <Stack flexDirection="column" flex={1}>
                    <Stack flexDirection="row" justifyContent="space-between">
                        <ListItemTextHeadline>
                            {content.user.screenName ?? "[not set]"}
                        </ListItemTextHeadline>
                        <TypographyHeaderSubTitleBox>
                            {relativeDate}
                            {relativeDate === "gestern"
                                ? ` ${datePosted.format("HH:mm")}`
                                : ""}
                        </TypographyHeaderSubTitleBox>
                    </Stack>
                    {content.contentType === ContentType.COMMENT && (
                        <ListItemTextRelatedPost>
                            {content.parentContent}
                        </ListItemTextRelatedPost>
                    )}
                    <Stack flexDirection="row" alignItems="flex-start">
                        <Box flex={1}>
                            <ListItemTextSubline>
                                {text ? parse(text) : "[not set]"}
                            </ListItemTextSubline>
                        </Box>
                        {content.commentCount > 0 && (
                            <CountsStack>
                                <TypographyHeaderSubTitleBox>
                                    {content.commentCount}
                                </TypographyHeaderSubTitleBox>
                                <CommentSolidIcon
                                    width={"16px"}
                                    height={"16px"}
                                    color={
                                        "var(--community-color-light-steal-blue)"
                                    }
                                />
                            </CountsStack>
                        )}
                        {!content.commentCount && content.reactionCount && (
                            <CountsStack>
                                <Stack flexDirection="row">
                                    {Object.keys(content.reactionCount).map(
                                        (reactionName: string) => (
                                            <GetReactionIcon
                                                reactionName={reactionName}
                                                sx={{
                                                    height: "16px",
                                                    width: "16px",
                                                }}
                                            />
                                        ),
                                    )}
                                </Stack>
                                <TypographyHeaderSubTitleBox>
                                    {aggregateReactions(content.reactionCount)}
                                </TypographyHeaderSubTitleBox>
                            </CountsStack>
                        )}
                    </Stack>
                </Stack>
            </ListItemMainSection>
        </ListItemButtonContainer>
    );
}
